const FAILOVER_EVENT_LABELS = {
  PEP_403_FAILOVER_RECOVERED: 'PEP 403 failover recovered',
  PEP_403_FAILOVER_ALL_403: 'PEP 403 failover exhausted',
  PEP_403_FAILOVER_NO_CANDIDATES: 'PEP 403 failover no sessions'
}

const DATA_PULL_FAIL_LABELS = {
  PEP_DATA_PULL_FAIL_403_SESSION: 'PEP pull failed (session)',
  PEP_DATA_PULL_FAIL_403_RANGE: 'PEP pull failed (date range)',
  PEP_DATA_PULL_FAIL_403: 'PEP pull failed (403)',
  PEP_DATA_PULL_FAIL_401: 'PEP pull failed (401)',
  PEP_DATA_PULL_FAIL: 'PEP pull failed'
}

const PEP_SESSION_OPS_LABELS = {
  PEP_KEEP_ALIVE_FAILED: 'PEP keep-alive failed',
  PEP_SESSION_RECOVERY_STARTED: 'PEP recovery started',
  PEP_SESSION_RECOVERY_SUCCEEDED: 'PEP recovery succeeded',
  PEP_SESSION_RECOVERY_MANUAL_LOGIN: 'Manual PEP login requested',
  PEP_SESSION_RECOVERY_SUPPRESSED: 'PEP recovery suppressed',
  PEP_SESSION_PERMISSION_DENIED: 'PEP permission denied'
}

const APP_LIFECYCLE_LABELS = {
  ELECTRON_APP_STARTED: 'Desktop app started',
  ELECTRON_APP_EXITING: 'Desktop app closing'
}

function parseTaggedFields(raw) {
  const fields = {}
  if (!raw || typeof raw !== 'string') {
    return fields
  }
  const pattern = /(\w+)=([^\s]+)/g
  let match = pattern.exec(raw)
  while (match != null) {
    fields[match[1]] = match[2]
    match = pattern.exec(raw)
  }
  return fields
}

export function pepElectronOpsEventShortLabel(eventCode) {
  const code = (eventCode || '').trim()
  return (
    PEP_SESSION_OPS_LABELS[code] ||
    FAILOVER_EVENT_LABELS[code] ||
    DATA_PULL_FAIL_LABELS[code] ||
    APP_LIFECYCLE_LABELS[code] ||
    (code === 'PEP_DATA_PULL_OK' ? 'PEP pull succeeded' : '')
  )
}

export function isAppLifecycleTelemetryEvent(ev) {
  const type = (ev.eventType ?? ev.EventType ?? '').trim()
  const code = (ev.eventCode ?? ev.EventCode ?? '').trim()
  return type === 'Lifecycle' || code === 'ELECTRON_APP_STARTED' || code === 'ELECTRON_APP_EXITING'
}

export function formatAppLifecycleOperationalMessage(ev) {
  const raw = (ev.message ?? ev.Message ?? '').trim()
  const pipeIdx = raw.indexOf(' | ')
  if (pipeIdx > 0) {
    return raw.slice(0, pipeIdx).trim()
  }
  if (raw) {
    return raw
  }
  const code = (ev.eventCode ?? ev.EventCode ?? '').trim()
  if (code === 'ELECTRON_APP_STARTED') {
    return 'The desktop app started and resumed background Hilton PEP data pulls.'
  }
  return 'The desktop app is closing; background Hilton PEP data pulls are paused until it is reopened.'
}

export function formatPepSessionOpsOperationalMessage(ev) {
  const raw = (ev.message ?? ev.Message ?? '').trim()
  const pipeIdx = raw.indexOf(' | ')
  if (pipeIdx > 0) {
    return raw.slice(0, pipeIdx).trim()
  }
  return raw || 'PEP session activity was recorded on the desktop app.'
}

export function isPepSessionOpsTelemetryEvent(ev) {
  const type = (ev.eventType ?? ev.EventType ?? '').trim()
  const code = (ev.eventCode ?? ev.EventCode ?? '').trim()
  return type === 'PepSessionOps' || code.startsWith('PEP_SESSION_') || code === 'PEP_KEEP_ALIVE_FAILED'
}

export function formatPep403FailoverOperationalMessage(ev) {
  const raw = (ev.message ?? ev.Message ?? '').trim()
  if (!raw) {
    return 'PEP shared-session failover activity was recorded.'
  }
  if (raw.startsWith('PEP shared-session failover')) {
    return raw.split(' outcome=')[0].trim()
  }
  const fields = parseTaggedFields(raw)
  if (fields.outcome === 'recovered') {
    return 'PEP shared-session failover recovered after a 403 authorization error.'
  }
  if (fields.outcome === 'allCandidates403') {
    return 'PEP shared-session failover failed because every shared session candidate returned 403.'
  }
  if (fields.outcome === 'noCandidates') {
    return 'PEP shared-session failover failed because no shared session candidates were available.'
  }
  return raw
}

export function describePepDataPullFailClass(failClass) {
  const fc = (failClass || '').trim().toLowerCase()
  if (fc === 'session_auth') {
    return 'Hilton PEP rejected the request because the sign-in session appears expired (not a date-range limit).'
  }
  if (fc === 'range_depth') {
    return 'Hilton PEP rejected an older date range; newer ranges may still work (not a sign-in failure).'
  }
  if (fc === 'range_future_edge') {
    return 'Hilton PEP rejected a future date boundary; this is usually a range limit, not sign-in.'
  }
  if (fc === 'range_chunk') {
    return 'Hilton PEP rejected a scheduled gap-fill chunk; check whether the session is still valid.'
  }
  return ''
}

export function isRangeOnlyPepDataPullFailure(ev) {
  const code = (ev.eventCode ?? ev.EventCode ?? '').trim()
  if (code === 'PEP_DATA_PULL_FAIL_403_RANGE') {
    return true
  }
  const raw = (ev.message ?? ev.Message ?? '').toLowerCase()
  if (raw.includes('failclass=range_')) {
    return true
  }
  return false
}

export function isSessionAuthPepDataPullFailure(ev) {
  const code = (ev.eventCode ?? ev.EventCode ?? '').trim()
  if (code === 'PEP_DATA_PULL_FAIL_403_SESSION') {
    return true
  }
  const raw = (ev.message ?? ev.Message ?? '').toLowerCase()
  if (raw.includes('failclass=session_auth')) {
    return true
  }
  return false
}

/**
 * A permission-denied 403 means the Hilton sign-in itself is valid but the account lacks
 * reservation-search access for this property. This is distinct from an expired session:
 * re-login will not fix it, so the modal must surface a different action (ask Hilton / switch
 * Hilton user) rather than "sign in again".
 */
export function isPepPermissionDeniedEvent(ev) {
  const code = (ev.eventCode ?? ev.EventCode ?? '').trim()
  if (code === 'PEP_SESSION_PERMISSION_DENIED') {
    return true
  }
  const raw = (ev.message ?? ev.Message ?? '').toLowerCase()
  return raw.includes('does not have permission to run reservation search')
}
